/**
 * H4 decision tree (Bundle 2 W2-C).
 *
 * Maps the pre-mount and post-mount check results to an AuditDecision with
 * verdict (allow / block / warn / fallback) and one of 7 fallback UX paths
 * F1-F7 per the H4 visual decision tree.
 */
import { allPreMountChecksPassed, firstFailingPreMountCheck } from './audit';
import {
  AuditDecision,
  CheckResult,
  CookieAuditResultPayload,
} from './types';

/** Map a pre-mount check sequence to an AuditDecision. */
export function decidePreMount(results: ReadonlyArray<CheckResult>): AuditDecision {
  if (allPreMountChecksPassed(results)) {
    return {
      verdict: 'allow',
      reason_code: 'PRE_MOUNT_ALL_CHECKS_PASS',
      user_visible_reason: 'Scout pre-mount audit succeeded; mounting iframe.',
      audit_event_type: 'h4.pre_mount_allow',
    };
  }
  const failing = firstFailingPreMountCheck(results);
  if (failing === null) {
    // Sequence incomplete (e.g., stopped at Check 3 defer_retry); treat as defer.
    return {
      verdict: 'fallback',
      reason_code: 'SCOUT_ORIGIN_UNREACHABLE',
      user_visible_reason: 'Scout availability check timed out; retrying briefly.',
      fallback_path: 'F3',
      retry_after_ms: 1_000,
      audit_event_type: 'h4.pre_mount_defer_retry',
    };
  }
  if (failing.outcome === 'defer_retry') {
    return {
      verdict: 'fallback',
      reason_code: 'SCOUT_ORIGIN_UNREACHABLE',
      user_visible_reason: 'Scout availability check timed out; retrying briefly.',
      fallback_path: 'F3',
      retry_after_ms: 1_000,
      check_position: failing.check_number,
      audit_event_type: 'h4.pre_mount_defer_retry',
    };
  }
  // outcome === 'fail' — map check number to fallback path + reason.
  return preMountFailDecision(failing);
}

function preMountFailDecision(failing: CheckResult): AuditDecision {
  switch (failing.check_number) {
    case 1:
      return {
        verdict: 'block',
        reason_code: 'COOKIE_API_DISABLED',
        user_visible_reason: 'Your browser is blocking cookies. Enable cookies and reload.',
        fallback_path: 'F1',
        check_position: 1,
        audit_event_type: 'h4.pre_mount_block',
      };
    case 2:
      return {
        verdict: 'block',
        reason_code: 'SCOUT_ORIGIN_NOT_CONFIGURED',
        user_visible_reason: 'Scout is temporarily unavailable. Operations notified.',
        fallback_path: 'F2',
        check_position: 2,
        audit_event_type: 'h4.pre_mount_block',
      };
    case 3:
      return {
        verdict: 'block',
        reason_code: 'SCOUT_ORIGIN_UNREACHABLE_AFTER_RETRY',
        user_visible_reason: 'Scout is temporarily unavailable. Operations notified.',
        fallback_path: 'F3',
        check_position: 3,
        audit_event_type: 'h4.pre_mount_block',
      };
    case 4:
      return {
        verdict: 'block',
        reason_code: 'COOKIE_API_BLOCKED_THIRD_PARTY',
        user_visible_reason:
          'Your browser blocks 3rd-party cookies. Open Scout in a new tab.',
        fallback_path: 'F4',
        check_position: 4,
        audit_event_type: 'h4.pre_mount_block',
      };
    case 5:
      return {
        verdict: 'block',
        reason_code: 'NESTED_IN_THIRD_PARTY_FRAME',
        user_visible_reason: 'Open the workspace directly (not embedded).',
        fallback_path: 'F5',
        check_position: 5,
        audit_event_type: 'h4.pre_mount_block',
      };
  }
}

/** Map a post-mount check outcome to an AuditDecision. */
export function decidePostMount(args: {
  ping: CookieAuditResultPayload | 'timeout';
  user_match: boolean;
}): AuditDecision {
  if (args.ping === 'timeout') {
    return {
      verdict: 'fallback',
      reason_code: 'POST_MOUNT_TIMEOUT',
      user_visible_reason:
        'Scout did not confirm a working session. Open Scout in a new tab to sign in, then return.',
      fallback_path: 'F6',
      check_position: 6,
      audit_event_type: 'h4.post_mount_block',
    };
  }
  if (!args.ping.scout_session_present) {
    return {
      verdict: 'fallback',
      reason_code: 'POST_MOUNT_NO_SESSION',
      user_visible_reason:
        'Scout requires you to sign in. Open Scout in a new tab to sign in, then return.',
      fallback_path: 'F6',
      check_position: 7,
      audit_event_type: 'h4.post_mount_block',
    };
  }
  if (!args.ping.storage_accessible) {
    return {
      verdict: 'fallback',
      reason_code: 'POST_MOUNT_STORAGE_INACCESSIBLE',
      user_visible_reason: 'Scout cannot run in the embedded view. Open Scout in a new tab.',
      fallback_path: 'F6',
      check_position: 8,
      audit_event_type: 'h4.post_mount_block',
    };
  }
  if (!args.user_match) {
    return {
      verdict: 'block',
      reason_code: 'POST_MOUNT_USER_MISMATCH',
      user_visible_reason: 'Session error — please re-login.',
      fallback_path: 'F7',
      check_position: 9,
      audit_event_type: 'h4.post_mount_user_mismatch',
    };
  }
  return {
    verdict: 'allow',
    reason_code: 'POST_MOUNT_ALL_CHECKS_PASS',
    user_visible_reason: 'Scout post-mount audit succeeded.',
    audit_event_type: 'h4.post_mount_allow',
  };
}

/** A deterministic, serializable summary of a decision (T-H4-10). */
export function serializeDecision(d: AuditDecision): string {
  return JSON.stringify({
    verdict: d.verdict,
    reason_code: d.reason_code,
    fallback_path: d.fallback_path ?? null,
    check_position: d.check_position ?? null,
    audit_event_type: d.audit_event_type,
  });
}
